import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Product } from '@shared/components/ui/Product';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/utils/utils';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProfitabilityByProductRow } from '../../models/Profitability';
import {
  formatCountWithShare,
  formatProfitabilityAmount,
  formatProfitabilityPercent,
  profitabilityNetMarginPercent,
} from '../../utils/formatMoney';
import { ProfitabilityProductBreakdownModal } from '../interaction/ProfitabilityProductBreakdownModal';

interface ProfitabilityByProductCardsProps {
  rows: ProfitabilityByProductRow[];
  isLoading: boolean;
  locale: string;
}

function MetricCell({
  label,
  value,
  emphasize,
  action,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
  action?: ReactNode;
}) {
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex min-h-[1rem] items-start justify-between gap-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        {action}
      </div>
      <p
        className={cn(
          'break-words tabular-nums text-foreground',
          emphasize ? 'text-base font-semibold text-title' : 'text-sm'
        )}
      >
        {value}
      </p>
    </div>
  );
}

function formatProductRowMetrics(
  r: ProfitabilityByProductRow,
  locale: string,
  currency: string
): {
  orders: string;
  shipped: string;
  delivered: string;
  returned: string;
  revenue: string;
  net_profit: string;
} {
  const f = r.fulfillment;
  const ordersTotal = f?.orders;
  const netMargin = profitabilityNetMarginPercent(r.allocated_revenue, r.profit);
  const profitAmount = formatProfitabilityAmount(r.profit, locale, currency);
  const netProfit =
    netMargin != null
      ? `${profitAmount} (${formatProfitabilityPercent(netMargin, locale)})`
      : profitAmount;

  return {
    orders: f != null ? String(f.orders) : '—',
    shipped:
      f != null
        ? formatCountWithShare(f.order_status_shipped, ordersTotal, locale)
        : '—',
    delivered:
      f != null
        ? formatCountWithShare(f.logistic_delivered, ordersTotal, locale)
        : '—',
    returned:
      f != null ? formatCountWithShare(f.logistic_returned, ordersTotal, locale) : '—',
    revenue: formatProfitabilityAmount(r.allocated_revenue, locale, currency),
    net_profit: netProfit,
  };
}

export const ProfitabilityByProductCards = ({ rows, isLoading, locale }: ProfitabilityByProductCardsProps) => {
  const { t } = useTranslation('profitabilityModule');
  const currency = useCurrency();
  const [breakdownRow, setBreakdownRow] = useState<ProfitabilityByProductRow | null>(null);

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-3" data-testid="profitability-by-product-grid">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-24 w-full min-w-0 animate-pulse rounded-lg border border-border bg-muted/40"
            data-testid={`profitability-by-product-skeleton-${i}`}
          />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground" data-testid="profitability-by-product-empty">
        {t('empty.no_products')}
      </p>
    );
  }

  return (
    <>
      <div className="flex w-full min-w-0 flex-col gap-3" data-testid="profitability-by-product-grid">
        {rows.map((r) => {
          const m = formatProductRowMetrics(r, locale, currency);
          return (
            <article
              key={r.product_id}
              className="w-full min-w-0 rounded-lg border border-border bg-card/50 p-3 shadow-sm transition-colors sm:p-4"
              data-testid={`profitability-by-product-card-${r.product_id}`}
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-6">
                <header className="min-w-0 shrink-0 border-b border-border pb-3 lg:w-[min(30%,340px)] lg:border-b-0 lg:border-r lg:pb-0 lg:pr-6">
                  <Product
                    id={r.product_id}
                    name={r.product_name}
                    code={r.code ?? undefined}
                    src={r.image_url?.trim() ? r.image_url : ''}
                    className="w-full min-w-0 max-w-full justify-start"
                    showTooltip
                    showImageModal={Boolean(r.image_url?.trim())}
                    data-testid={`profitability-by-product-product-${r.product_id}`}
                  />
                </header>

                <div className="min-w-0 flex-1 overflow-x-auto [-webkit-overflow-scrolling:touch]">
                  <div className="grid min-w-[36rem] grid-cols-6 gap-x-3 gap-y-2 sm:min-w-0 sm:w-full sm:gap-x-4">
                    <MetricCell label={t('by_product.row.orders')} value={m.orders} />
                    <MetricCell label={t('by_product.row.shipped')} value={m.shipped} />
                    <MetricCell label={t('by_product.row.delivered')} value={m.delivered} />
                    <MetricCell label={t('by_product.row.returned')} value={m.returned} />
                    <MetricCell label={t('by_product.row.revenue')} value={m.revenue} />
                    <MetricCell
                      label={t('by_product.row.net_profit')}
                      value={m.net_profit}
                      emphasize
                      action={
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto shrink-0 px-1.5 py-0 text-xs font-medium text-primary hover:text-primary"
                          onClick={() => setBreakdownRow(r)}
                          data-testid={`profitability-by-product-breakdown-${r.product_id}`}
                        >
                          {t('by_product.breakdown.open')}
                        </Button>
                      }
                    />
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <ProfitabilityProductBreakdownModal
        row={breakdownRow}
        open={breakdownRow != null}
        onOpenChange={(open) => !open && setBreakdownRow(null)}
        locale={locale}
        currency={currency}
      />
    </>
  );
};
